/**
 * Navigation Signals — stage-health TREND aggregation for the Score
 * Trends page. All pillar/stage/weight CONSTANTS come from
 * lib/missingMiddle.js (single source of truth); this module only holds
 * the trend-aggregation logic (first → latest change) layered on top.
 */
import { JOURNEY_STAGES } from './journeyStages';
import {
  PILLAR_MEASURES, PILLAR_WEIGHTS, PILLAR_STAGE, SIGNAL_STAGES,
  NAV_WEIGHTS, MEASURE_PILLAR, MEASURE_STAGE_MAP, STAGE_DIAGNOSTIC_TAB,
} from './missingMiddle';

export { PILLAR_MEASURES, PILLAR_WEIGHTS, PILLAR_STAGE, SIGNAL_STAGES, NAV_WEIGHTS, MEASURE_PILLAR, MEASURE_STAGE_MAP, STAGE_DIAGNOSTIC_TAB };

export const STAGE_META = {
  ...Object.fromEntries(JOURNEY_STAGES.map((s) => [s.key, { label: s.label, color: s.color }])),
  value: { label: 'Value', color: '#e11d48' },
};

export const stageOf = (measureId) => MEASURE_STAGE_MAP[measureId] || null;

/**
 * Aggregate per-date rows ({date, <measureId>: score}) into stage
 * series: stage value = mean of available member scores; navigation =
 * pillar-weighted mean of present stage values (weights renormalized).
 */
export function buildStageChartData(chartData) {
  return (chartData || []).map((row) => {
    const out = { date: row.date };
    const stageVals = {};
    SIGNAL_STAGES.forEach((st) => {
      const vals = Object.entries(MEASURE_STAGE_MAP)
        .filter(([mid, s]) => s === st && typeof row[mid] === 'number')
        .map(([mid]) => row[mid]);
      if (vals.length) {
        stageVals[st] = vals.reduce((a, b) => a + b, 0) / vals.length;
        out[st] = Math.round(stageVals[st] * 10) / 10;
      }
    });
    const present = SIGNAL_STAGES.filter((s) => stageVals[s] != null);
    if (present.length) {
      const wSum = present.reduce((a, s) => a + NAV_WEIGHTS[s], 0);
      out.navigation = Math.round(
        (present.reduce((a, s) => a + stageVals[s] * NAV_WEIGHTS[s], 0) / wSum) * 10,
      ) / 10;
    }
    return out;
  });
}

/**
 * Stage health snapshot from trendsData.trends (uses each measure's
 * existing trend stats — latest_score / first_score — untouched).
 * Returns { capability|visibility|confidence|coordination|value|navigation:
 *   { latest, first, changePct, signalCount } | null }
 */
export function buildStageHealth(trends) {
  const buckets = {};
  SIGNAL_STAGES.forEach((st) => { buckets[st] = { latest: [], first: [] }; });
  Object.entries(trends || {}).forEach(([mid, m]) => {
    const st = MEASURE_STAGE_MAP[mid];
    const t = m?.trend;
    if (!st || !t) return;
    if (typeof t.latest_score === 'number') buckets[st].latest.push(t.latest_score);
    if (typeof t.first_score === 'number') buckets[st].first.push(t.first_score);
  });

  const out = {};
  SIGNAL_STAGES.forEach((st) => {
    const { latest, first } = buckets[st];
    if (!latest.length) { out[st] = null; return; }
    // Round member-mean to 1 decimal to match the backend pillar score
    // (round(pillar_avg, 1)), so Score Trends and the LEI page render
    // identical stage numbers.
    const r1 = (n) => Math.round(n * 10) / 10;
    const latestAvg = r1(latest.reduce((a, b) => a + b, 0) / latest.length);
    const firstAvg = first.length ? r1(first.reduce((a, b) => a + b, 0) / first.length) : null;
    out[st] = {
      latest: latestAvg,
      first: firstAvg,
      changePct: firstAvg ? ((latestAvg - firstAvg) / firstAvg) * 100 : null,
      signalCount: latest.length,
    };
  });

  const present = SIGNAL_STAGES.filter((s) => out[s]);
  if (present.length) {
    const wSum = present.reduce((a, s) => a + NAV_WEIGHTS[s], 0);
    const latest = present.reduce((a, s) => a + out[s].latest * NAV_WEIGHTS[s], 0) / wSum;
    const withFirst = present.filter((s) => out[s].first != null);
    let first = null;
    if (withFirst.length) {
      const w2 = withFirst.reduce((a, s) => a + NAV_WEIGHTS[s], 0);
      first = withFirst.reduce((a, s) => a + out[s].first * NAV_WEIGHTS[s], 0) / w2;
    }
    out.navigation = {
      latest,
      first,
      changePct: first ? ((latest - first) / first) * 100 : null,
      signalCount: present.length,
    };
  } else {
    out.navigation = null;
  }
  return out;
}

/** Directional banding of the composite trend — navigation is directional. */
export function navigationMomentum(changePct) {
  if (changePct == null) return { label: 'Unknown', tone: 'slate' };
  if (changePct >= 5) return { label: 'Strong Positive', tone: 'emerald' };
  if (changePct >= 1.5) return { label: 'Positive', tone: 'teal' };
  if (changePct > -1.5) return { label: 'Stable', tone: 'slate' };
  if (changePct > -5) return { label: 'Negative', tone: 'amber' };
  return { label: 'Critical', tone: 'rose' };
}

/**
 * The single condition stage most constraining navigation this period.
 * Preference: steepest-declining stage; if none are declining, the
 * lowest-scoring stage. Returns { key, label, latest, changePct,
 * declining } or null. Matches leiConditions.strongestWeakest's weakest
 * when no stage is declining.
 */
export function mostConstrainingStage(stageHealth) {
  if (!stageHealth) return null;
  const present = SIGNAL_STAGES
    .map((key) => ({ key, ...(stageHealth[key] || {}) }))
    .filter((s) => s.latest != null);
  if (!present.length) return null;

  const declining = present.filter((s) => s.changePct != null && s.changePct < 0);
  let pick;
  if (declining.length) {
    pick = declining.reduce((a, b) => (b.changePct < a.changePct ? b : a));
  } else {
    pick = present.reduce((a, b) => (b.latest < a.latest ? b : a));
  }
  return {
    key: pick.key,
    label: STAGE_META[pick.key]?.label || pick.key,
    latest: pick.latest,
    changePct: pick.changePct ?? null,
    declining: declining.length > 0,
  };
}
